"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Tooltip } from "react-tooltip";
import { usePrefetchWaveData } from "@/hooks/usePrefetchWaveData";
import { useWaveData } from "@/hooks/useWaveData";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { ApiWaveType } from "@/generated/models/ObjectSerializer";
import WavePicture from "@/components/waves/WavePicture";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getActiveWaveIdFromUrl, getWaveHomeRoute, getWaveRoute } from "@/helpers/navigation.helpers";
interface BrainContentPinnedWaveProps {
  readonly waveId: string;
  readonly active: boolean;
  readonly onMouseEnter: (waveId: string) => void;
  readonly onMouseLeave: () => void;
  readonly onRemove: (waveId: string) => void;
}
const BrainContentPinnedWave: React.FC<BrainContentPinnedWaveProps> = t0 => {
  const $ = _c(60);
  const {
    waveId,
    active,
    onMouseEnter,
    onMouseLeave,
    onRemove
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    registerWave
  } = useMyStream();
  const {
    isApp
  } = useDeviceInfo();
  let t1;
  if ($[0] !== onRemove || $[1] !== waveId) {
    t1 = () => onRemove(waveId);
    $[0] = onRemove;
    $[1] = waveId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[3] = t1;
    $[4] = waveId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: wave
  } = useWaveData(t2);
  const isMobile = useIsMobileDevice();
  const isDropWave = wave && wave.wave.type !== ApiWaveType.Chat;
  let t3;
  if ($[6] !== isApp || $[7] !== pathname || $[8] !== searchParams || $[9] !== wave?.chat.scope.group?.is_direct_message) {
    t3 = waveId_0 => {
      const currentWaveId = getActiveWaveIdFromUrl({
        pathname,
        searchParams
      }) ?? undefined;
      const isDirectMessage = wave?.chat.scope.group?.is_direct_message ?? false;
      if (currentWaveId === waveId_0) {
        return getWaveHomeRoute({
          isDirectMessage,
          isApp
        });
      }
      return getWaveRoute({
        waveId: waveId_0,
        isDirectMessage,
        isApp
      });
    };
    $[6] = isApp;
    $[7] = pathname;
    $[8] = searchParams;
    $[9] = wave?.chat.scope.group?.is_direct_message;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const getHref = t3;
  let t4;
  if ($[11] !== getHref || $[12] !== onMouseLeave || $[13] !== router || $[14] !== waveId) {
    t4 = e => {
      e.preventDefault();
      onMouseLeave();
      router.push(getHref(waveId));
    };
    $[11] = getHref;
    $[12] = onMouseLeave;
    $[13] = router;
    $[14] = waveId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const onLinkClick = t4;
  let t5;
  if ($[16] !== onMouseEnter || $[17] !== pathname || $[18] !== prefetchWaveData || $[19] !== registerWave || $[20] !== searchParams || $[21] !== waveId) {
    t5 = () => {
      onMouseEnter(waveId);
      const currentWaveId_0 = getActiveWaveIdFromUrl({
        pathname,
        searchParams
      }) ?? undefined;
      if (waveId === currentWaveId_0) {
        return;
      }
      registerWave(waveId);
      prefetchWaveData(waveId);
    };
    $[16] = onMouseEnter;
    $[17] = pathname;
    $[18] = prefetchWaveData;
    $[19] = registerWave;
    $[20] = searchParams;
    $[21] = waveId;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const onHover = t5;
  let t6;
  if ($[23] !== onRemove || $[24] !== waveId) {
    t6 = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      onRemove(waveId);
    };
    $[23] = onRemove;
    $[24] = waveId;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const onRemoveClick = t6;
  const t7 = `tw-group tw-relative tw-transition-all tw-duration-300 ${!active ? "tw-opacity-70" : ""}`;
  let t8;
  if ($[26] !== getHref || $[27] !== waveId) {
    t8 = getHref(waveId);
    $[26] = getHref;
    $[27] = waveId;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const t9 = `wave-tooltip-${waveId}`;
  const t10 = `tw-relative tw-flex tw-h-6 tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-lg tw-px-1.5 tw-transition-all tw-duration-300 ${active ? "tw-bg-primary-500/15 tw-ring-1 tw-ring-inset tw-ring-primary-400/30" : "tw-bg-iron-800 tw-ring-1 tw-ring-inset tw-ring-iron-700"} `;
  let t11;
  if ($[29] !== wave) {
    t11 = <div className="tw-relative tw-flex tw-size-3.5 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-iron-900">{wave ? <WavePicture name={wave.name} picture={wave.picture} contributors={wave.contributors_overview.map(_temp)} /> : <div className="tw-h-full tw-w-full tw-bg-iron-800" />}</div>;
    $[29] = wave;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = `tw-relative tw-whitespace-nowrap tw-text-[11px] tw-font-medium ${active ? "tw-text-primary-400" : "tw-text-iron-200"}`;
  const t13 = wave?.name;
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = <span className={t12}>{t13}</span>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isDropWave) {
    t15 = isDropWave && <svg className="tw-size-3 tw-flex-shrink-0 tw-text-[#E8D48A]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" /></svg>;
    $[34] = isDropWave;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <svg className="tw-size-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M17 7L7 17M7 7L17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== onRemoveClick) {
    t17 = <button type="button" onClick={onRemoveClick} aria-label="Remove wave" className="tw-relative -tw-mr-0.5 tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0.5 tw-text-iron-400 tw-opacity-100 tw-transition-all tw-duration-300 desktop-hover:hover:tw-text-red">{t16}</button>;
    $[37] = onRemoveClick;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t14 || $[42] !== t15 || $[43] !== t17 || $[44] !== t9) {
    t18 = <div data-tooltip-id={t9} className={t10}>{t11}{t14}{t15}{t17}</div>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t14;
    $[42] = t15;
    $[43] = t17;
    $[44] = t9;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== isMobile || $[47] !== wave?.name || $[48] !== waveId) {
    t19 = !isMobile && <Tooltip id={`wave-tooltip-${waveId}`} place="top" style={{
      backgroundColor: "#1F2937",
      color: "white",
      padding: "4px 8px"
    }}><span className="tw-text-xs">{wave?.name ?? ""}</span></Tooltip>;
    $[46] = isMobile;
    $[47] = wave?.name;
    $[48] = waveId;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== onLinkClick || $[51] !== t18 || $[52] !== t19 || $[53] !== t8) {
    t20 = <Link href={t8} onClick={onLinkClick} className="tw-group tw-flex tw-items-center tw-no-underline">{t18}{t19}</Link>;
    $[50] = onLinkClick;
    $[51] = t18;
    $[52] = t19;
    $[53] = t8;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== onHover || $[56] !== onMouseLeave || $[57] !== t20 || $[58] !== t7) {
    t21 = <div className={t7} onMouseEnter={onHover} onMouseLeave={onMouseLeave}>{t20}</div>;
    $[55] = onHover;
    $[56] = onMouseLeave;
    $[57] = t20;
    $[58] = t7;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  return t21;
};
export default BrainContentPinnedWave;
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}